import React, { useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { profileAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { getInitials } from '../lib/utils';
import { Camera, Save, User, Mail, Shield, Award, Phone, MapPin, FileText } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const ROLE_OPTIONS = [
  { value: 'player',       label: 'Player' },
  { value: 'coach',        label: 'Coach' },
  { value: 'referee',      label: 'Referee' },
  { value: 'parent',       label: 'Parent' },
  { value: 'club_admin',   label: 'Club Admin' },
  { value: 'venue_admin',  label: 'Venue Admin' },
  { value: 'school_admin', label: 'School Admin' },
];

// Roles that aren't user-self-assignable; show them disabled if currently assigned.
const PRIVILEGED_ROLES = {
  platform_admin: 'Platform Admin',
  tournament_admin: 'Tournament Admin',
  umpire: 'Umpire',
};

export default function MyProfile() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [role, setRole] = useState(user?.role || 'player');
  const [skillLevel, setSkillLevel] = useState(user?.skill_level || 'intermediate');
  const [phone, setPhone] = useState(user?.phone || '');
  const [city, setCity] = useState(user?.city || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [picture, setPicture] = useState(user?.picture || '');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const handlePictureUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2MB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => setPicture(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name,
        skill_level: skillLevel,
        picture,
        phone,
        city,
        bio,
      };
      // Only send role if user changed it AND it's a self-assignable role
      if (role !== user?.role) {
        payload.role = role;
      }
      await profileAPI.update(payload);
      toast.success('Profile updated');
      if (refreshUser) await refreshUser();
    } catch (err) {
      console.error('Profile update failed:', err);
      toast.error(err?.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const currentIsPrivileged = PRIVILEGED_ROLES[user?.role];

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="my-profile-page">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight">My Profile</h1>

        <Card>
          <CardContent className="p-6 space-y-6">
            {/* Avatar */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative group cursor-pointer" onClick={() => fileRef.current?.click()}>
                <Avatar className="h-24 w-24">
                  <AvatarImage src={picture} />
                  <AvatarFallback className="text-2xl">{getInitials(user?.name)}</AvatarFallback>
                </Avatar>
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="h-6 w-6 text-white" />
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePictureUpload} data-testid="picture-upload" />
              </div>
              <p className="text-xs text-muted-foreground">Click to change photo</p>
            </div>

            {/* Email (read-only) */}
            <div className="space-y-1">
              <Label className="flex items-center gap-1 text-muted-foreground"><Mail className="h-3.5 w-3.5" />Email</Label>
              <Input value={user?.email || ''} disabled className="bg-muted" />
            </div>

            {/* Editable name + role */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="flex items-center gap-1"><User className="h-3.5 w-3.5" />Display Name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} data-testid="profile-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="flex items-center gap-1"><Shield className="h-3.5 w-3.5" />Role</Label>
                {currentIsPrivileged ? (
                  <Input value={currentIsPrivileged} disabled className="bg-muted" data-testid="profile-role-locked" />
                ) : (
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger data-testid="profile-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Skill + Phone */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="flex items-center gap-1"><Award className="h-3.5 w-3.5" />Skill Level</Label>
                <Select value={skillLevel} onValueChange={setSkillLevel}>
                  <SelectTrigger data-testid="skill-level-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />Phone</Label>
                <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 9xxxxxxxxx" data-testid="profile-phone-input" />
              </div>
            </div>

            {/* City */}
            <div className="space-y-1">
              <Label className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />City</Label>
              <Input value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. Chennai" data-testid="profile-city-input" />
            </div>

            {/* Bio */}
            <div className="space-y-1">
              <Label className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" />Bio</Label>
              <Textarea
                value={bio}
                onChange={e => setBio(e.target.value)}
                placeholder="Tell us about yourself…"
                rows={3}
                maxLength={300}
                data-testid="profile-bio-input"
              />
              <p className="text-[10px] text-muted-foreground text-right">{bio.length}/300</p>
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full bg-secondary hover:bg-secondary/90" data-testid="save-profile-btn">
              <Save className="mr-2 h-4 w-4" />{saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
